import { useAuth } from '@/contexts/auth-context';
import { useEffect, useState } from 'react';

// Mirror the same whitelist used in sidebar-nav, admin page, and admin/setup
const SUPER_ADMIN_EMAILS = ["breakinbreadswe@gmail.com"];

function isSuperAdminEmail(email?: string | null) {
  return SUPER_ADMIN_EMAILS.includes((email || "").toLowerCase().trim());
}

function isSuperAdminRole(roles?: string[], accountType?: string) {
  if ((roles || []).some(r => r.toLowerCase() === "superadmin" || r.toLowerCase() === "super admin")) return true;
  if ((accountType || "").toLowerCase() === "superadmin") return true;
  return false;
}

/**
 * Hook to check if the current user is an admin or super admin.
 * Checks:
 *   1. Firebase custom claims: `admin` or `superAdmin`
 *   2. Email whitelist (breakinbreadswe@gmail.com, etc.)
 *   3. Firestore userProfile: roles / accountType === "superadmin"
 */
export function useAdmin() {
  const { user, userProfile, viewAs, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for Firebase Auth to finish initialising before making any decision
    if (authLoading) return;

    async function checkAdmin() {
      if (!user) {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setLoading(false);
        return;
      }

      // "Preview as role" (super-admin impersonation) must actually DROP
      // privileges — otherwise the email whitelist / custom claims below keep
      // returning superAdmin and every admin-only WYSIWYG control stays visible.
      // 'superadmin' is not an impersonable option, so previewing any role means
      // NOT super admin; only 'admin' keeps admin powers.
      if (viewAs) {
        setIsSuperAdmin(false);
        setIsAdmin(viewAs === 'admin');
        setLoading(false);
        return;
      }

      try {
        // 1. Firebase custom claims
        const tokenResult = await user.getIdTokenResult();
        const claims = tokenResult.claims;
        const claimSuperAdmin = claims.superAdmin === true;
        const claimAdmin = claims.admin === true || claimSuperAdmin;

        // 2. Email whitelist
        const emailSuperAdmin = isSuperAdminEmail(user.email);

        // 3. Firestore roles / accountType
        const firestoreSuperAdmin = isSuperAdminRole(
          userProfile?.roles as string[] | undefined,
          userProfile?.accountType as string | undefined,
        );

        const superAdmin = claimSuperAdmin || emailSuperAdmin || firestoreSuperAdmin;
        const admin = claimAdmin || superAdmin;

        setIsSuperAdmin(superAdmin);
        setIsAdmin(admin);
      } catch (error) {
        console.error('Error checking admin status:', error);
        // Fall back to email/Firestore checks even if token fails
        const emailSuperAdmin = isSuperAdminEmail(user.email);
        const firestoreSuperAdmin = isSuperAdminRole(
          userProfile?.roles as string[] | undefined,
          userProfile?.accountType as string | undefined,
        );
        const superAdmin = emailSuperAdmin || firestoreSuperAdmin;
        setIsSuperAdmin(superAdmin);
        setIsAdmin(superAdmin);
      } finally {
        setLoading(false);
      }
    }

    checkAdmin();
  }, [user, userProfile, viewAs, authLoading]);

  return { isAdmin, isSuperAdmin, loading };
}
